import { DocumentDuplicateIcon, TrashIcon } from '@heroicons/react/24/outline';

import { copy } from '@/levity/flows/copy';
import { useDuplicateFlowFeatureFlag } from '@/levity/flows/subscriptions';
import { AppNodeData } from '@/levity/flows/types';
import { AppIcon, AppIconGroup, AppIconVariant, Collection, Switch, Tooltip, TooltipPosition } from '@/ui-library';

import { OptionsMenu } from '../../options-menu';
import type { FlowListItemProps } from './flow-list-item.types';

export const FlowListItem: React.FC<FlowListItemProps> = (props) => {
  const { flow, onActivate, onDelete, onDuplicate } = props;
  const {
    title,
    appIcons: { trigger, center, end },
    hasBeenPublished,
  } = flow;

  const { flagValue: isDuplicateFlowEnabled } = useDuplicateFlowFeatureFlag();

  const optionsMenuItems = [
    {
      text: 'Delete',
      icon: <TrashIcon className='w-4 h-4' />,
      onClick: () => onDelete(flow.id),
    },
    ...(isDuplicateFlowEnabled
      ? [
          {
            text: 'Duplicate',
            icon: <DocumentDuplicateIcon className='w-4 h-4' />,
            onClick: () => onDuplicate(flow.id),
          },
        ]
      : []),
  ];

  return (
    <Collection.Item>
      <div className='flex flex-row items-center gap-4 min-h-[40px]'>
        <AppIconGroup>
          {trigger.node && (trigger.node.metadata as unknown as AppNodeData)?.integration ? (
            <AppIcon
              key={trigger.node.id}
              variant={AppIconVariant.APP}
              icon={{
                src: trigger.iconSrc,
                alt: trigger.iconAlt,
              }}
            />
          ) : (
            <AppIcon variant={AppIconVariant.PLACEHOLDER} />
          )}

          {center.showPlaceholder && <AppIcon variant={AppIconVariant.PLACEHOLDER} />}
          {center.showCount && <AppIcon variant={AppIconVariant.COUNT} count={center.count} tooltip={center.tooltip} />}
          {center.showApp && (
            <AppIcon
              key={center.node?.id}
              variant={AppIconVariant.APP}
              icon={{
                src: center.iconSrc,
                alt: center.iconAlt,
              }}
            />
          )}

          {!end.node && <AppIcon variant={AppIconVariant.PLACEHOLDER} />}
          {end.node && (
            <AppIcon
              key={end.node.id}
              variant={AppIconVariant.APP}
              icon={{
                src: end.iconSrc,
                alt: end.iconAlt,
              }}
            />
          )}
        </AppIconGroup>

        <span>{title}</span>
      </div>
      <div className='flex items-center gap-4'>
        <div className='hidden group-hover/list-item:flex'>
          <OptionsMenu options={optionsMenuItems} />
        </div>
        <Tooltip
          hide={hasBeenPublished}
          content={{ description: copy.activate.disabled }}
          position={TooltipPosition.BottomRight}>
          <Switch
            checked={flow.isActivated}
            onChange={(isActivated) => onActivate(flow.id, isActivated)}
            disabled={!hasBeenPublished}
          />
        </Tooltip>
      </div>
    </Collection.Item>
  );
};
